import { Trans } from '@lingui/react/macro';
import { AlertTriangle, CheckCircle2, Info, Loader2, MapPin } from 'lucide-react';
import { Alert, AlertDescription } from '@/common/components/ui/alert';
import { Button } from '@/common/components/ui/button';
import type { GeocodeTier } from '@/features/geolocation/config';
import type { LocationPickerApi } from '@/features/geolocation/hooks/useLocationPicker';

export interface LocationStatusProps {
  picker: LocationPickerApi;
}

function TierLabel({ tier }: { tier: GeocodeTier | null }) {
  if (tier === 'cityPostal') return <Trans>city and postal code only</Trans>;
  if (tier === 'cityOnly') return <Trans>city only</Trans>;
  return <Trans>full address</Trans>;
}

/** Reports what the lookup produced and how much the user should still correct. */
export function LocationStatus({ picker }: LocationStatusProps) {
  const { status, precision, resolvedTier, displayName, tier, elevation } = picker;

  return (
    <div className="space-y-2">
      {/*
        Announced to assistive technology whenever the position changes. The
        wording is deliberately distinct from the visible copy below so the same
        message is never announced twice.
      */}
      <div aria-live="polite" className="sr-only">
        {status === 'locating' && <Trans>Looking up the position of the address.</Trans>}
        {status === 'resolved' && displayName}
        {status === 'manual' && <Trans>The position was marked on the map.</Trans>}
        {status === 'notFound' && <Trans>No position was found for this address.</Trans>}
      </div>

      {status === 'locating' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {tier === 'full' ? (
            <Trans>Looking up the address…</Trans>
          ) : (
            <Trans>Trying a wider search…</Trans>
          )}
        </p>
      )}

      {status === 'resolved' && precision === 'building' && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>{displayName}</span>
        </p>
      )}

      {status === 'resolved' && precision !== 'building' && (
        <Alert>
          <AlertTriangle />
          <AlertDescription>
            <Trans>
              Only an approximate position was found (<TierLabel tier={resolvedTier} />
              ). Click the map to mark the exact spot of the installation.
            </Trans>
            {displayName && <span className="text-muted-foreground">{displayName}</span>}
          </AlertDescription>
        </Alert>
      )}

      {status === 'notFound' && (
        <Alert>
          <Info />
          <AlertDescription>
            <Trans>
              This address could not be found on the map. Click the map to mark the position
              manually.
            </Trans>
          </AlertDescription>
        </Alert>
      )}

      {status === 'failed' && (
        <Alert>
          <AlertTriangle />
          <AlertDescription>
            <Trans>
              The map lookup service is unavailable. You can mark the position manually.
            </Trans>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0"
              onClick={picker.onLocateClick}
            >
              <Trans>Try again</Trans>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {status === 'manual' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0 text-primary" />
          <span className="font-medium text-foreground">
            <Trans>Position marked manually</Trans>
          </span>
        </p>
      )}

      {picker.addressChangedWhilePinned && (
        <p className="text-sm text-muted-foreground">
          <Trans>
            The address changed. The marked position was kept — use “Locate from address” to move
            it.
          </Trans>
        </p>
      )}

      {elevation.isError && (
        <Alert>
          <AlertTriangle />
          <AlertDescription>
            <Trans>
              Altitude could not be determined automatically. Enter it manually or save without it.
            </Trans>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0"
              onClick={elevation.retry}
            >
              <Trans>Try again</Trans>
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
